/**
 * VariableTracker - Tracks declared variables in MethodScript code
 *
 * Handles:
 * - var declarations: var my_var
 * - array declarations: array my_array 10i
 * - meas_loop output variables: meas_loop_ca pot curr ...
 *
 * Results are cached based on the model version ID and upToLine parameter.
 * isVariableDeclared short-circuits on first match when the cache is cold.
 */

import { buildOptionalArgRegex } from './syntax-definitions.js';
import type { MonacoModel } from './types.js';

const OPTIONAL_ARG_REGEX = buildOptionalArgRegex();

type CachedScan = {
    readonly versionId: number;
    readonly upToLine: number;
    readonly variables: Set<string>;
    readonly arrays: Set<string>;
};

export class VariableTracker {
    private cache: CachedScan | null = null;

    /**
     * Return the existing cache if it matches the given model version and upToLine,
     * or null if the cache is stale / empty.
     */
    private getValidCache(model: MonacoModel, upToLine: number): CachedScan | null {
        if (this.cache?.versionId === model.getVersionId() && this.cache.upToLine === upToLine) {
            return this.cache;
        }
        return null;
    }

    /**
     * Scan lines and populate the cache, or return the existing cache if valid.
     */
    private scanLines(model: MonacoModel, upToLine: number): CachedScan {
        const cached = this.getValidCache(model, upToLine);
        if (cached) {
            return cached;
        }

        const variables = new Set<string>();
        const arrays = new Set<string>();

        for (let i = 1; i <= upToLine; i++) {
            const line = model.getLineContent(i).trim();

            // Skip comments
            if (line.startsWith('#')) {
                continue;
            }

            // var declarations: var my_var
            const varMatch = /^var\s+([a-z][a-z0-9_]*)/.exec(line);
            if (varMatch) {
                variables.add(varMatch[1]);
                continue;
            }

            // array declarations: array my_array 10i
            const arrayMatch = /^array\s+([a-z][a-z0-9_]*)/.exec(line);
            if (arrayMatch) {
                variables.add(arrayMatch[1]);
                arrays.add(arrayMatch[1]);
                continue;
            }

            // measurement loop output variables
            this.extractMeasLoopVariables(line, variables);
        }

        this.cache = { versionId: model.getVersionId(), upToLine, variables, arrays };
        return this.cache;
    }

    /**
     * Extract output variable names from a meas_loop line and add them to the set.
     */
    private extractMeasLoopVariables(line: string, target: Set<string>): void {
        const measLoopMatch = /^meas_loop_\w+\s+(.*)/.exec(line);
        if (!measLoopMatch) {
            return;
        }

        let paramsText = measLoopMatch[1];

        // Stop before optional arguments (e.g., add_meas(, poly_we(, time()
        // Optional arguments are: command_name followed by (
        const optionalArgStart = paramsText.search(OPTIONAL_ARG_REGEX);
        if (optionalArgStart !== -1) {
            paramsText = paramsText.substring(0, optionalArgStart);
        }

        const params = paramsText.split(/\s+/);
        for (const param of params) {
            // Only add valid variable names (not literals or numbers)
            // Variable names must start with lowercase letter
            if (/^[a-z][a-z0-9_]*$/.test(param)) {
                target.add(param);
            }
        }
    }

    /**
     * Collect all declared variables up to a specific line
     *
     * @param model - Monaco editor model
     * @param upToLine - Line number to collect variables up to (inclusive)
     * @returns Set of declared variable names
     */
    public collectDeclaredVariables(model: MonacoModel, upToLine: number): Set<string> {
        return this.scanLines(model, upToLine).variables;
    }

    /**
     * Get declared variables as sorted array filtered by prefix
     *
     * @param model - Monaco editor model
     * @param upToLine - Line number to collect variables up to (inclusive)
     * @param prefix - Optional prefix to filter variables (case-insensitive)
     * @returns Sorted array of variable names
     */
    public getFilteredVariables(model: MonacoModel, upToLine: number, prefix = ''): Array<string> {
        const declaredVars = this.collectDeclaredVariables(model, upToLine);

        if (!prefix) {
            return Array.from(declaredVars).sort();
        }

        const lowerPrefix = prefix.toLowerCase();
        return Array.from(declaredVars)
            .filter((varName) => varName.toLowerCase().startsWith(lowerPrefix))
            .sort();
    }

    /**
     * Collect only array declarations (not scalar variables)
     *
     * @param model - Monaco editor model
     * @param upToLine - Line number to collect arrays up to (inclusive)
     * @returns Set of declared array names
     */
    public collectDeclaredArrays(model: MonacoModel, upToLine: number): Set<string> {
        return this.scanLines(model, upToLine).arrays;
    }

    /**
     * Get declared arrays as sorted array filtered by prefix
     *
     * @param model - Monaco editor model
     * @param upToLine - Line number to collect arrays up to (inclusive)
     * @param prefix - Optional prefix to filter arrays (case-insensitive)
     * @returns Sorted array of array names
     */
    public getFilteredArrays(model: MonacoModel, upToLine: number, prefix = ''): Array<string> {
        const declaredArrays = this.collectDeclaredArrays(model, upToLine);

        if (!prefix) {
            return Array.from(declaredArrays).sort();
        }

        const lowerPrefix = prefix.toLowerCase();
        return Array.from(declaredArrays)
            .filter((arrayName) => arrayName.toLowerCase().startsWith(lowerPrefix))
            .sort();
    }

    /**
     * Check if a variable is declared up to a specific line.
     *
     * When the cache is warm, this is an O(1) lookup.
     * When the cache is cold, this short-circuits on the first match
     * instead of collecting all variables.
     *
     * @param model - Monaco editor model
     * @param upToLine - Line number to check up to (inclusive)
     * @param variableName - Variable name to check
     * @returns true if variable is declared
     */
    public isVariableDeclared(model: MonacoModel, upToLine: number, variableName: string): boolean {
        // Use cache when available for O(1) lookup
        const cached = this.getValidCache(model, upToLine);
        if (cached) {
            return cached.variables.has(variableName);
        }

        // Short-circuit scan: stop as soon as we find the variable
        for (let i = 1; i <= upToLine; i++) {
            const line = model.getLineContent(i).trim();

            if (line.startsWith('#')) {
                continue;
            }

            const varMatch = /^var\s+([a-z][a-z0-9_]*)/.exec(line);
            if (varMatch?.[1] === variableName) {
                return true;
            }

            const arrayMatch = /^array\s+([a-z][a-z0-9_]*)/.exec(line);
            if (arrayMatch?.[1] === variableName) {
                return true;
            }

            const measLoopMatch = /^meas_loop_\w+\s+(.*)/.exec(line);
            if (measLoopMatch) {
                let paramsText = measLoopMatch[1];

                const optionalArgStart = paramsText.search(OPTIONAL_ARG_REGEX);
                if (optionalArgStart !== -1) {
                    paramsText = paramsText.substring(0, optionalArgStart);
                }

                const params = paramsText.split(/\s+/);
                for (const param of params) {
                    if (/^[a-z][a-z0-9_]*$/.test(param) && param === variableName) {
                        return true;
                    }
                }
            }
        }

        return false;
    }
}
